import { type FC, type ReactNode } from 'react';
import classnames from 'classnames';

import { getSrcsetString, getSrcsetUrl } from 'dibs-image-utils/exports/srcSet.ts';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';

import { TradeBenefitsAccordionItem } from '../../TradeBenefitsAccordionItem.tsx';
import { useServerVars } from '../../../shared/ServerVarsContext.tsx';
import { trackTradeBenefitsEvent } from '../../tradeBenefitsTracking.ts';

import styles from './TradeBenefitsOverviewSection.scss';
import dibsCss from 'dibs-css';

type AccordionItem = {
    id: string;
    summary: ReactNode;
    content: ReactNode;
};

const IMAGE_WIDTHS = [400, 600, 800, 1200];
const IMAGE_SIZES = '(max-width: 600px) 100vw, 25vw';

const TradeBenefitsOverviewSection: FC<{
    imagePath: string;
    title: ReactNode;
    items: AccordionItem[];
}> = ({ imagePath, title, items }) => {
    const { isMobile } = useServerVars();

    return (
        <div>
            {!isMobile && (
                <div
                    className={classnames(
                        styles.image,
                        dibsCss.mbSmall,
                        dibsCss.wFull,
                        dibsCss.aspectSquare,
                        dibsCss.roundedXsmall,
                        dibsCss.overflowHidden
                    )}
                >
                    <img
                        src={getSrcsetUrl(imagePath, 600)}
                        srcSet={getSrcsetString(imagePath, IMAGE_WIDTHS)}
                        sizes={IMAGE_SIZES}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className={classnames(dibsCss.hFull, dibsCss.wFull, dibsCss.objectCover)}
                    />
                </div>
            )}
            <HeadingLevel>
                {Heading => (
                    <Heading
                        className={classnames(
                            dibsCss.sassyFontHeaderMedium,
                            dibsCss.mt0,
                            dibsCss.mbSmall
                        )}
                    >
                        {title}
                    </Heading>
                )}
            </HeadingLevel>
            <ul className={classnames(styles.list, dibsCss.p0, dibsCss.m0)}>
                {items.map(({ id, summary, content }) => (
                    <TradeBenefitsAccordionItem
                        key={id}
                        summary={summary}
                        contentId={id}
                        onOpen={() =>
                            trackTradeBenefitsEvent({
                                stepInteractionName: 'benefit drawer opened',
                                trigger: id,
                            })
                        }
                    >
                        {content}
                    </TradeBenefitsAccordionItem>
                ))}
            </ul>
        </div>
    );
};

export { TradeBenefitsOverviewSection };
